import { some } from 'lodash';

import { StudyWorkflowModules } from 'interfaces/study';

import { isRnaOnlyStudyWorkflow } from './Workflow/studyWorkflowConfiguration';
import {
  AnnotationAssignmentsTab,
  CellNeighborhoodsTab,
  CellRulesTab,
  DatasetTab,
  JobsTab,
  MifMarkerUnsupervisedAnalysisTab,
  QaAnalysisTab,
  QCMarkersTab,
  StudyDashboardTabOptions,
  StudyReportTab,
} from './useStudyDashboardTabState';

export type DeferredStudyDashboardTabRequirementState = 'available' | 'pending' | 'unavailable';
export type DeferredStudyDashboardTabAvailability = DeferredStudyDashboardTabRequirementState;
export type DeferredStudyDashboardTabRequirementKind = 'permission' | 'study-fit' | 'data';

export interface DeferredStudyDashboardTabRequirement {
  description: string;
  kind: DeferredStudyDashboardTabRequirementKind;
  state: DeferredStudyDashboardTabRequirementState;
}

export interface DeferredStudyDashboardTabConfig {
  requirements: DeferredStudyDashboardTabRequirement[];
  tab: StudyDashboardTabOptions;
}

export interface DeferredStudyDashboardTabDecision extends DeferredStudyDashboardTabConfig {
  availability: DeferredStudyDashboardTabAvailability;
  shouldRenderContent: boolean;
  shouldShowTab: boolean;
}

interface StudyFitDeferredStudyDashboardTabDecisionParams {
  activeTab?: StudyDashboardTabOptions | null;
  additionalRequirements?: DeferredStudyDashboardTabRequirement[];
  permissionRequirement: DeferredStudyDashboardTabRequirement;
  studyFitRequirement: DeferredStudyDashboardTabRequirement;
  tab: StudyDashboardTabOptions;
}

interface StudyFitOrDataDeferredStudyDashboardTabDecisionParams
  extends StudyFitDeferredStudyDashboardTabDecisionParams {
  dataRequirement: DeferredStudyDashboardTabRequirement;
}

export const shouldShowStudySpecificMultiplexTabs = ({
  isMultiplex,
  specificStudySelected,
}: {
  isMultiplex: boolean;
  specificStudySelected: boolean;
}) => specificStudySelected && isMultiplex;

export const getRnaOnlyStudySetupRequirementState = ({
  isLoadingStudy,
  specificStudySelected,
  workflowModules,
}: {
  isLoadingStudy: boolean;
  specificStudySelected: boolean;
  workflowModules?: StudyWorkflowModules;
}): DeferredStudyDashboardTabRequirementState => {
  if (specificStudySelected && isLoadingStudy) {
    return 'pending';
  }

  return isRnaOnlyStudyWorkflow(workflowModules) ? 'unavailable' : 'available';
};

export const shouldShowRnaIncompatibleStudyDashboardTabs = (
  rnaOnlyStudySetupRequirementState: DeferredStudyDashboardTabRequirementState
) => rnaOnlyStudySetupRequirementState !== 'unavailable';

interface GetStudyDashboardTabsParams {
  canViewAnnotationAssignmentsDashboard: boolean;
  canViewCellRulesTab: boolean;
  canViewJobs: boolean;
  canViewQCMarkersTab: boolean;
  workflowTabDecision?: DeferredStudyDashboardTabDecision;
  deferredTabDecisions?: DeferredStudyDashboardTabDecision[];
}

export const getDeferredStudyDashboardTabAvailability = (
  requirements: DeferredStudyDashboardTabRequirement[]
): DeferredStudyDashboardTabAvailability => {
  if (some(requirements, ({ state }) => state === 'unavailable')) {
    return 'unavailable';
  }

  if (some(requirements, ({ state }) => state === 'pending')) {
    return 'pending';
  }

  return 'available';
};

export const getDeferredStudyDashboardTabDecision = ({
  requirements,
  tab,
  activeTab,
}: DeferredStudyDashboardTabConfig & {
  activeTab?: StudyDashboardTabOptions | null;
}): DeferredStudyDashboardTabDecision => {
  const availability = getDeferredStudyDashboardTabAvailability(requirements);
  const isRequestedTab = activeTab === tab;
  const hasPendingNonDataRequirement = some(requirements, ({ kind, state }) => kind !== 'data' && state === 'pending');

  return {
    availability,
    requirements,
    shouldShowTab: availability === 'available' || (availability === 'pending' && isRequestedTab),
    // Keep the requested tab selected while availability is unresolved, but do not render
    // report content until access/study-fit checks are settled.
    shouldRenderContent:
      isRequestedTab && (availability === 'available' || (availability === 'pending' && !hasPendingNonDataRequirement)),
    tab,
  };
};

export const getStudyFitDeferredStudyDashboardTabDecision = ({
  activeTab,
  additionalRequirements = [],
  permissionRequirement,
  studyFitRequirement,
  tab,
}: StudyFitDeferredStudyDashboardTabDecisionParams): DeferredStudyDashboardTabDecision =>
  getDeferredStudyDashboardTabDecision({
    activeTab,
    requirements: [permissionRequirement, studyFitRequirement, ...additionalRequirements],
    tab,
  });

export const getStudyFitOrDataDeferredStudyDashboardTabDecision = ({
  activeTab,
  additionalRequirements = [],
  dataRequirement,
  permissionRequirement,
  studyFitRequirement,
  tab,
}: StudyFitOrDataDeferredStudyDashboardTabDecisionParams): DeferredStudyDashboardTabDecision =>
  getDeferredStudyDashboardTabDecision({
    activeTab,
    requirements: [
      ...(studyFitRequirement.state === 'available' || studyFitRequirement.state === 'pending'
        ? [permissionRequirement, studyFitRequirement]
        : [permissionRequirement, dataRequirement]),
      ...additionalRequirements,
    ],
    tab,
  });

export const getStudyDashboardTabs = ({
  canViewAnnotationAssignmentsDashboard,
  canViewCellRulesTab,
  canViewJobs,
  canViewQCMarkersTab,
  workflowTabDecision,
  deferredTabDecisions = [],
}: GetStudyDashboardTabsParams): StudyDashboardTabOptions[] => {
  const tabs: StudyDashboardTabOptions[] = [DatasetTab];
  const deferredTabDecisionByTab = new Map(deferredTabDecisions.map((decision) => [decision.tab, decision]));

  const appendDeferredTab = (tab: StudyDashboardTabOptions) => {
    const decision = deferredTabDecisionByTab.get(tab);

    if (decision?.shouldShowTab) {
      tabs.push(tab);
    }

    deferredTabDecisionByTab.delete(tab);
  };

  if (workflowTabDecision?.shouldShowTab) tabs.push(workflowTabDecision.tab);

  if (canViewAnnotationAssignmentsDashboard) tabs.push(AnnotationAssignmentsTab);
  if (canViewJobs) tabs.push(JobsTab);
  if (canViewQCMarkersTab) tabs.push(QCMarkersTab);
  appendDeferredTab(MifMarkerUnsupervisedAnalysisTab);
  if (canViewCellRulesTab) tabs.push(CellRulesTab);
  appendDeferredTab(QaAnalysisTab);
  appendDeferredTab(CellNeighborhoodsTab);
  appendDeferredTab(StudyReportTab);

  deferredTabDecisionByTab.forEach(({ shouldShowTab, tab }) => {
    if (shouldShowTab) {
      tabs.push(tab);
    }
  });

  return tabs;
};
